import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import components from '../../../components';
import AssetDeviceSelector from '../../../components/assetDeviceSelector';
import { setAlert } from '../../../redux/dashboard/actions';
import { getSelectedProject } from '../../../redux/projects/actions';
import API from '../../../services/api';
import utils from '../../../utils';
import Asset from '../asset';

const { CustomButton, CustomInput, DropdownSearch, EmptyState, SearchBox } = components;

const AssetsTab = ({ assets, sites, loading, loadAssets, isCreateModalOpen, closeCreateModal }) => {
  const [filteredAssets, setFilteredAssets] = useState(assets);
  const [displayName, setDisplayName] = useState('');
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const project = useSelector(getSelectedProject);
  const dispatch = useDispatch();

  useEffect(() => {
    setFilteredAssets(assets);
  }, [assets]);

  const resetForm = () => {
    setDisplayName('');
    setSelectedSite(null);
    setSelectedDevices([]);
    closeCreateModal();
  };

  const createAsset = async () => {
    if (!project || !displayName.trim() || !selectedSite) {
      dispatch(
        setAlert(utils.generateAlert('Could not create Asset. Name and Site are required.', 'error'))
      );
      return;
    }

    setSubmitting(true);
    try {
      const selectedDeviceIds = selectedDevices.map(device => device.id);
      const asset = await API.createAsset(project.id, selectedSite.id, {
        attachedDeviceIds: selectedDeviceIds,
        displayName: displayName.trim(),
        siteId: selectedSite.id,
        type: 'UNKNOWN'
      });
      const devicesToMove = selectedDevices.filter(device => device.requiresDetach).map(device => device.id);
      if (devicesToMove.length > 0) {
        await API.detachAssetDevicesFromOtherSites(project.id, selectedSite.id, asset.id, devicesToMove);
      }
      if (selectedDeviceIds.length > 0) {
        await API.updateAssetAttachedDevices(project.id, selectedSite.id, asset.id, selectedDeviceIds);
      }
      dispatch(setAlert(utils.generateAlert('Asset created successfully!', 'success')));
      resetForm();
      loadAssets();
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert('Could not create Asset. Please try again.', 'error')));
    } finally {
      setSubmitting(false);
    }
  };

  const searchAssets = searchBy => {
    if (!searchBy) {
      setFilteredAssets(assets);
      return;
    }

    const normalizedSearch = searchBy.toUpperCase();
    setFilteredAssets(
      assets.filter(asset => {
        const site = sites.find(item => item.id === asset.siteId);
        return [asset.displayName, asset.id, asset.siteId, site?.displayName, ...(asset.attachedDeviceIds || [])].some(
          value => value && value.toUpperCase().includes(normalizedSearch)
        );
      })
    );
  };

  const renderAssetList = () => (
    <>
      <SearchBox placeholder="Search by Asset, Site, or Device" className="outline" onChange={searchAssets} />
      {filteredAssets.length > 0 ? (
        filteredAssets.map(asset => (
          <Asset
            key={asset.id}
            asset={asset}
            site={sites.find(item => item.id === asset.siteId)}
            loadAssets={loadAssets}
          />
        ))
      ) : (
        <div className="no-results-found">
          <p>No assets were found</p>
        </div>
      )}
    </>
  );

  const renderEmptyState = () => (
    <EmptyState
      icon="deviceOutlineDark"
      title="You don't have any Assets."
      details={'Create your first Asset by clicking "Add Asset".'}
    />
  );

  return (
    <div className="assets-tab">
      {loading ? <p>Loading...</p> : assets.length > 0 ? renderAssetList() : renderEmptyState()}
      <Modal size="lg" isOpen={isCreateModalOpen} fade={false} centered toggle={resetForm} className="integration-create-modal">
        <ModalHeader className="header-gray" tag="div">
          <h5>Add Asset</h5>
        </ModalHeader>
        <ModalBody>
          <div className="account-fields asset-fields">
            <div className="form-row">
              <div className="col">
                <div className="form-group">
                  <label>Name</label>
                  <CustomInput
                    id="new-asset-name"
                    type="text"
                    value={displayName}
                    isEditable
                    onChange={event => setDisplayName(event.target.value)}
                  />
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label>Site</label>
                  <DropdownSearch
                    title="Search Site"
                    selected={
                      selectedSite
                        ? { label: selectedSite.displayName, value: selectedSite.id }
                        : { label: 'Select Site', value: '' }
                    }
                    leftAlign
                    onSelect={site => {
                      setSelectedSite(site);
                      setSelectedDevices([]);
                    }}
                    options={sites.map(site => ({ label: site.displayName || site.id, value: site }))}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="col">
                <div className="form-group asset-device-section">
                  <label>Devices (optional)</label>
                  <AssetDeviceSelector
                    key={selectedSite?.id || 'new-asset-device-selector'}
                    projectId={project?.id}
                    siteId={selectedSite?.id}
                    site={selectedSite}
                    onSiteUpdated={setSelectedSite}
                    selectedDevices={selectedDevices}
                    onChange={setSelectedDevices}
                    disabled={!project || !selectedSite}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <CustomButton classes="btn-secondary" title="Cancel" handleClick={resetForm} disabled={submitting} />
          <CustomButton
            classes="btn-primary"
            title="Create"
            handleClick={createAsset}
            loading={submitting}
            disabled={submitting || !displayName.trim() || !selectedSite}
          />
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AssetsTab;
